import React, { useState, useEffect, useCallback, useRef } from "react";
import { api, formatMediaUrl, detailText, idr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Palette,
  Image as ImageIcon,
  FileText,
  Plus,
  Trash2,
  Pencil,
  Copy,
  Sparkles,
  Share2,
  CheckCircle2,
  Undo2,
  Redo2,
  Grid2X2,
  Maximize2,
  Pin,
  ChevronRight,
  Upload,
  Layers,
  Wand2,
  FolderPlus,
  Flower2,
  X,
  Loader2,
  Check,
  Hand,
  Pointer,
  GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/context/LanguageContext";

const CATEGORIES = [
  "Semua",
  "Pelaminan",
  "Gaun & Jas",
  "Venue",
  "Undangan",
  "Buket",
  "Dekorasi",
  "Groom",
  "Makeup",
  "Fotografi",
  "Catering",
];

const PREDEFINED_PALETTES = [
  {
    name: "Elegant Champagne",
    colors: [
      { color_name: "Ivory Silk", hex_code: "#FAF1EA" },
      { color_name: "Warm Gold", hex_code: "#D4A359" },
      { color_name: "Sage Pine", hex_code: "#44684D" },
      { color_name: "Aged Teak", hex_code: "#735C40" },
    ],
  },
  {
    name: "Garden Fresh",
    colors: [
      { color_name: "Sage Pine", hex_code: "#44684D" },
      { color_name: "Ivory Silk", hex_code: "#FAF1EA" },
      { color_name: "Warm Gold", hex_code: "#D4A359" },
      { color_name: "Aged Teak", hex_code: "#735C40" },
    ],
  },
  {
    name: "Modern Luxury",
    colors: [
      { color_name: "Midnight Black", hex_code: "#191C1A" },
      { color_name: "Gilded Brass", hex_code: "#D4A359" },
      { color_name: "Alabaster White", hex_code: "#F8FAF6" },
      { color_name: "Deep Bronze", hex_code: "#5A452A" },
    ],
  },
  {
    name: "Rustic Terracotta",
    colors: [
      { color_name: "Terracotta", hex_code: "#C86D51" },
      { color_name: "Warm Sand", hex_code: "#E8D5C4" },
      { color_name: "Olive Leaf", hex_code: "#735C40" },
      { color_name: "Forest Sage", "hex_code": "#44684D" },
    ],
  },
  {
    name: "Romantic Pastel",
    colors: [
      { color_name: "Blush Pink", hex_code: "#F4E2DE" },
      { color_name: "Soft Rose", hex_code: "#D8C4B6" },
      { color_name: "Dusty Slate", hex_code: "#4A5568" },
      { color_name: "Champagne Cream", hex_code: "#E2D4C9" },
    ],
  },
];

export default function Moodboard() {
  const { t } = useT();

  // State
  const [moodboards, setMoodboards] = useState([]);
  const [activeBoard, setActiveBoard] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(CATEGORIES);
  const [selectedCategory, setSelectedCategory] = useState("Semua");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Canvas View State
  const [zoomLevel, setZoomLevel] = useState(100);
  const [showGrid, setShowGrid] = useState(true);
  const [isPanMode, setIsPanMode] = useState(false);
  const [isDraggingCanvas, setIsDraggingCanvas] = useState(false);

  // History & Undo / Redo Stack State
  const [historyStack, setHistoryStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // Card Drag & Drop Reorder State
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  const [dragOverItemIndex, setDragOverItemIndex] = useState(null);

  const recordHistory = () => {
    setHistoryStack((prev) => [
      ...prev,
      { items: [...items], board: activeBoard ? { ...activeBoard } : null },
    ]);
    setRedoStack([]);
  };

  const handleUndo = async () => {
    if (historyStack.length === 0) {
      return toast.info("Tidak ada langkah yang dapat di-undo");
    }
    const lastState = historyStack[historyStack.length - 1];
    const nextHistory = historyStack.slice(0, historyStack.length - 1);

    setRedoStack((prev) => [
      ...prev,
      { items: [...items], board: activeBoard ? { ...activeBoard } : null },
    ]);
    setHistoryStack(nextHistory);

    setItems(lastState.items);
    if (lastState.board) setActiveBoard(lastState.board);

    if (activeBoard) {
      try {
        const itemIds = lastState.items.map((it) => it.item_id);
        await api.post(`/moodboard/${activeBoard.moodboard_id}/items/reorder`, {
          item_ids: itemIds,
        });
      } catch {}
    }
    toast.success("Langkah sebelumnya dipulihkan ↩️");
  };

  const handleRedo = async () => {
    if (redoStack.length === 0) {
      return toast.info("Tidak ada langkah yang dapat di-redo");
    }
    const nextState = redoStack[redoStack.length - 1];
    const nextRedo = redoStack.slice(0, redoStack.length - 1);

    setHistoryStack((prev) => [
      ...prev,
      { items: [...items], board: activeBoard ? { ...activeBoard } : null },
    ]);
    setRedoStack(nextRedo);

    setItems(nextState.items);
    if (nextState.board) setActiveBoard(nextState.board);

    if (activeBoard) {
      try {
        const itemIds = nextState.items.map((it) => it.item_id);
        await api.post(`/moodboard/${activeBoard.moodboard_id}/items/reorder`, {
          item_ids: itemIds,
        });
      } catch {}
    }
    toast.success("Langkah maju dipulihkan ↪️");
  };

  const handleCardDragStart = (e, index) => {
    recordHistory();
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleCardDragEnter = (e, index) => {
    e.preventDefault();
    if (draggedItemIndex === null || draggedItemIndex === index) return;
    setDragOverItemIndex(index);

    setItems((prevItems) => {
      const updated = [...prevItems];
      const [draggedItem] = updated.splice(draggedItemIndex, 1);
      updated.splice(index, 0, draggedItem);
      return updated;
    });
    setDraggedItemIndex(index);
  };

  const handleCardDragEnd = async () => {
    setDraggedItemIndex(null);
    setDragOverItemIndex(null);
    if (!activeBoard) return;

    try {
      const itemIds = items.map((it) => it.item_id);
      await api.post(`/moodboard/${activeBoard.moodboard_id}/items/reorder`, {
        item_ids: itemIds,
      });
      toast.success("Tata letak tersimpan otomatis ✨");
    } catch (e) {
      toast.error("Gagal menyimpan tata letak kartu");
    }
  };

  // Canvas Pan & Hand Tool Refs
  const canvasContainerRef = useRef(null);
  const isMouseDownRef = useRef(false);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const scrollTopRef = useRef(0);

  const handleCanvasMouseDown = (e) => {
    if (!isPanMode) return;
    if (e.target.closest("button, input, textarea, select, a, [role='button']")) return;
    if (!canvasContainerRef.current) return;

    isMouseDownRef.current = true;
    setIsDraggingCanvas(true);
    startXRef.current = e.pageX - canvasContainerRef.current.offsetLeft;
    startYRef.current = e.pageY - canvasContainerRef.current.offsetTop;
    scrollLeftRef.current = canvasContainerRef.current.scrollLeft;
    scrollTopRef.current = canvasContainerRef.current.scrollTop;
  };

  const handleCanvasMouseMove = (e) => {
    if (!isMouseDownRef.current || !canvasContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - canvasContainerRef.current.offsetLeft;
    const y = e.pageY - canvasContainerRef.current.offsetTop;
    const walkX = (x - startXRef.current) * 1.2;
    const walkY = (y - startYRef.current) * 1.2;
    canvasContainerRef.current.scrollLeft = scrollLeftRef.current - walkX;
    canvasContainerRef.current.scrollTop = scrollTopRef.current - walkY;
  };

  const handleCanvasMouseUp = () => {
    isMouseDownRef.current = false;
    setIsDraggingCanvas(false);
  };

  // Dialog & Sheet States
  const [createBoardOpen, setCreateBoardOpen] = useState(false);
  const [editBoardOpen, setEditBoardOpen] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  // Form States
  const [boardForm, setBoardForm] = useState({
    title: "",
    description: "",
    concept: "Garden Fresh & Warm Champagne",
    cover_image: "",
    colors: PREDEFINED_PALETTES[1].colors,
  });

  const [itemForm, setItemForm] = useState({
    type: "image",
    title: "",
    text_content: "",
    category: "Dekorasi",
    image_url: "",
    image_id: "",
    vendor: "",
    estimated_cost: 0,
    width: 6,
    height: 300,
  });

  const [uploadingFile, setUploadingFile] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const fileInputRef = useRef(null);

  // AI Recommendation State
  const [aiLoading, setAiLoading] = useState(false);
  const [aiForm, setAiForm] = useState({
    concept: "Garden Fresh Glasshouse",
    color_preference: "Sage Green & Warm Gold",
    style_preference: "High Fashion Editorial Atelier",
  });
  const [aiResult, setAiResult] = useState(null);

  // Load Boards List
  const loadMoodboards = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/moodboard");
      const list = data.moodboards || [];
      setMoodboards(list);
      if (list.length > 0 && !activeBoard) {
        loadBoardDetail(list[0].moodboard_id);
      } else if (activeBoard) {
        loadBoardDetail(activeBoard.moodboard_id);
      }
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memuat moodboard");
    } finally {
      setLoading(false);
    }
  }, [activeBoard]);

  // Load Detail of Selected Board
  const loadBoardDetail = async (id) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/moodboard/${id}`);
      setActiveBoard(data.moodboard);
      setItems(data.items || []);
      setHistoryStack([]);
      setRedoStack([]);
      if (data.categories) setCategories(["Semua", ...data.categories]);
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memuat detail moodboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMoodboards();
  }, []);

  // Board CRUD handlers
  const handleCreateBoard = async () => {
    if (!boardForm.title.trim()) {
      return toast.error("Judul moodboard wajib diisi");
    }
    try {
      setSaving(true);
      const { data } = await api.post("/moodboard", boardForm);
      toast.success("Moodboard baru berhasil dibuat ✨");
      setCreateBoardOpen(false);
      setBoardForm({
        title: "",
        description: "",
        concept: "Garden Fresh & Warm Champagne",
        cover_image: "",
        colors: PREDEFINED_PALETTES[0].colors,
      });
      loadMoodboards();
      if (data.moodboard) {
        loadBoardDetail(data.moodboard.moodboard_id);
      }
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal membuat moodboard");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateBoard = async () => {
    if (!activeBoard) return;
    recordHistory();
    try {
      setSaving(true);
      const { data } = await api.put(`/moodboard/${activeBoard.moodboard_id}`, {
        title: activeBoard.title,
        description: activeBoard.description,
        concept: activeBoard.concept,
        cover_image: activeBoard.cover_image,
        colors: activeBoard.colors,
      });
      setActiveBoard(data.moodboard);
      setEditBoardOpen(false);
      toast.success("Moodboard berhasil diperbarui");
      loadMoodboards();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal memperbarui moodboard");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBoard = async (id) => {
    if (!window.confirm("Apakah Anda yakin ingin menghapus moodboard ini beserta seluruh isinya?")) return;
    try {
      await api.delete(`/moodboard/${id}`);
      toast.success("Moodboard berhasil dihapus");
      setActiveBoard(null);
      loadMoodboards();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menghapus moodboard");
    }
  };

  const handleDuplicateBoard = async (id) => {
    try {
      setSaving(true);
      const { data } = await api.post(`/moodboard/${id}/duplicate`);
      toast.success("Moodboard berhasil diduplikasi!");
      loadMoodboards();
      if (data.moodboard) {
        loadBoardDetail(data.moodboard.moodboard_id);
      }
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menduplikasi moodboard");
    } finally {
      setSaving(false);
    }
  };

  // Image Upload handler
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeBoard) return;

    if (file.size > 5 * 1024 * 1024) {
      return toast.error("Ukuran file maksimal 5 MB");
    }

    try {
      setUploadingFile(true);
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await api.post(
        `/moodboard/${activeBoard.moodboard_id}/items/upload`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setItemForm((prev) => ({
        ...prev,
        type: "image",
        image_url: formatMediaUrl(data.image_url),
        image_id: data.image_id,
        title: prev.title || file.name.replace(/\.[^/.]+$/, ""),
      }));

      // Extract colors from uploaded image
      extractColorsFromImage(file);

      toast.success("Foto berhasil diunggah & warna terekstrak 🎨");
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal mengunggah foto");
    } finally {
      setUploadingFile(false);
    }
  };

  // Client-side HTML5 Canvas Color Extraction
  const extractColorsFromImage = (file) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = 100;
        canvas.height = 100;
        ctx.drawImage(img, 0, 0, 100, 100);

        const imgData = ctx.getImageData(0, 0, 100, 100).data;
        const colorCounts = {};

        for (let i = 0; i < imgData.length; i += 16) {
          const r = Math.round(imgData[i] / 32) * 32;
          const g = Math.round(imgData[i + 1] / 32) * 32;
          const b = Math.round(imgData[i + 2] / 32) * 32;
          const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
          colorCounts[hex] = (colorCounts[hex] || 0) + 1;
        }

        const sortedHex = Object.keys(colorCounts).sort((a, b) => colorCounts[b] - colorCounts[a]);
        const topHexes = sortedHex.slice(0, 4);

        if (topHexes.length >= 2 && activeBoard) {
          const extractedColors = topHexes.map((hex, idx) => ({
            color_name: `Tone ${idx + 1}`,
            hex_code: hex,
          }));
          setActiveBoard((prev) => ({
            ...prev,
            colors: extractedColors,
          }));
        }
      };
    };

    reader.readAsDataURL(file);
  };

  // Open dialog to edit item
  const handleStartEditItem = (item) => {
    setEditingItemId(item.item_id);
    setItemForm({
      type: item.type || "image",
      title: item.title || "",
      text_content: item.text_content || "",
      category: item.category || "Dekorasi",
      image_url: item.image_url || "",
      image_id: item.image_id || "",
      vendor: item.vendor || "",
      estimated_cost: item.estimated_cost || 0,
      width: item.width || 6,
      height: item.height || 300,
    });
    setAddItemOpen(true);
  };

  // Add/Update Canvas Item Handler
  const handleSaveItem = async () => {
    if (!activeBoard) return;
    if (itemForm.type === "image" && !itemForm.image_url) {
      return toast.error("URL / File gambar wajib diisi");
    }
    if (itemForm.type === "text" && !itemForm.text_content) {
      return toast.error("Isi catatan tidak boleh kosong");
    }

    recordHistory();
    try {
      setSaving(true);
      if (editingItemId) {
        const { data } = await api.put(
          `/moodboard/${activeBoard.moodboard_id}/items/${editingItemId}`,
          itemForm
        );
        setItems((prev) =>
          prev.map((it) => (it.item_id === editingItemId ? data.item : it))
        );
        toast.success("Elemen berhasil diperbarui! ✨");
      } else {
        const { data } = await api.post(
          `/moodboard/${activeBoard.moodboard_id}/items`,
          itemForm
        );
        setItems((prev) => [...prev, data.item]);
        toast.success("Elemen berhasil ditambahkan ke canvas! ✨");
      }

      setAddItemOpen(false);
      setEditingItemId(null);
      setItemForm({
        type: "image",
        title: "",
        text_content: "",
        category: "Dekorasi",
        image_url: "",
        image_id: "",
        vendor: "",
        estimated_cost: 0,
        width: 6,
        height: 300,
      });
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menyimpan elemen");
    } finally {
      setSaving(false);
    }
  };

  // Delete Item Handler
  const handleDeleteItem = async (itemId) => {
    if (!activeBoard) return;
    recordHistory();
    try {
      await api.delete(`/moodboard/${activeBoard.moodboard_id}/items/${itemId}`);
      setItems((prev) => prev.filter((it) => it.item_id !== itemId));
      toast.success("Elemen dihapus");
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menghapus elemen");
    }
  };

  // AI Recommendation Trigger
  const handleFetchAiRecommendations = async () => {
    try {
      setAiLoading(true);
      const { data } = await api.post("/moodboard/recommendations", aiForm);
      setAiResult(data);
      toast.success("Rekomendasi Weddly AI berhasil dibuat ✨");
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal mendapatkan rekomendasi AI");
    } finally {
      setAiLoading(false);
    }
  };

  // Apply AI Recommendation to Moodboard
  const handleApplyAiRecommendation = async () => {
    if (!aiResult || !activeBoard) return;

    recordHistory();
    try {
      setSaving(true);
      // 1. Update Board concept & palette
      if (aiResult.color_palette) {
        await api.put(`/moodboard/${activeBoard.moodboard_id}`, {
          concept: aiResult.concept_title || activeBoard.concept,
          colors: aiResult.color_palette,
        });
      }

      // 2. Add suggested items
      if (aiResult.suggested_items && aiResult.suggested_items.length > 0) {
        for (const sugg of aiResult.suggested_items) {
          await api.post(`/moodboard/${activeBoard.moodboard_id}/items`, {
            type: "text",
            title: sugg.title,
            text_content: sugg.description,
            category: sugg.category || "Dekorasi",
            width: 4,
            height: 200,
          });
        }
      }

      toast.success("Rekomendasi AI diterapkan ke moodboard! 🎉");
      setAiDrawerOpen(false);
      loadBoardDetail(activeBoard.moodboard_id);
    } catch (e) {
      toast.error("Gagal menerapkan rekomendasi AI");
    } finally {
      setSaving(false);
    }
  };

  // Copy Hex code helper
  const copyHex = (hex) => {
    navigator.clipboard.writeText(hex);
    toast.success(`Kode warna ${hex} disalin!`);
  };

  // Filter items
  const filteredItems = items.filter((it) => {
    if (selectedCategory === "Semua") return true;
    return (it.category || "").toLowerCase() === selectedCategory.toLowerCase();
  });

  if (loading && moodboards.length === 0) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-sm font-medium text-muted-foreground font-serif">
            Menyiapkan Workspace Wedding Moodboard…
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col font-sans pb-16 md:pb-0">
      {/* Context Bar & Header Controls */}
      <div className="w-full bg-card px-4 md:px-8 pt-4 pb-3 shadow-sm border-b border-border">
        <div className="flex flex-col gap-3">
          {/* Top Row: Board Switcher / Title & Action Controls */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div>
              {/* Board Title & Selector */}
              <div className="flex items-center gap-3 flex-wrap">
                <h1
                  className="font-serif text-2xl md:text-3xl font-medium tracking-tight cursor-pointer hover:text-primary transition-colors"
                  onClick={() => setEditBoardOpen(true)}
                  title="Klik untuk ubah informasi moodboard"
                >
                  {activeBoard?.title || "Elegant Garden Wedding"}
                </h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full text-muted-foreground hover:bg-muted"
                  onClick={() => setEditBoardOpen(true)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>

                {/* Moodboard Selector Dropdown */}
                {moodboards.length > 1 && (
                  <Select
                    value={activeBoard?.moodboard_id || ""}
                    onValueChange={(val) => loadBoardDetail(val)}
                  >
                    <SelectTrigger className="w-[180px] h-8 text-xs bg-background border-border">
                      <SelectValue placeholder="Pilih Moodboard" />
                    </SelectTrigger>
                    <SelectContent>
                      {moodboards.map((b) => (
                        <SelectItem key={b.moodboard_id} value={b.moodboard_id}>
                          {b.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs border-primary text-primary hover:bg-primary/10"
                  onClick={() => setCreateBoardOpen(true)}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Board Baru
                </Button>
              </div>
            </div>

            {/* Right Header Actions */}
            <div className="flex items-center flex-wrap gap-2 self-start lg:self-center">
              {/* Auto-save Status */}
              <div className="flex items-center gap-2 px-3 h-9 bg-muted rounded-lg text-xs select-none">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                <span className="font-medium text-[11px]">
                  Tersimpan otomatis
                </span>
              </div>

              <div className="w-px h-6 bg-border mx-1 hidden sm:block"></div>

              {/* AI Muse Drawer Trigger */}
              <Button
                variant="outline"
                className="h-9 gap-1.5 text-xs bg-card border-border text-foreground hover:bg-accent"
                onClick={() => setAiDrawerOpen(true)}
              >
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="font-semibold">Weddly AI Muse</span>
              </Button>

              {/* Duplicate & Delete Actions */}
              {activeBoard && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-muted-foreground hover:text-primary"
                    title="Duplikasi Board Ini"
                    onClick={() => handleDuplicateBoard(activeBoard.moodboard_id)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-muted-foreground hover:text-red-600"
                    title="Hapus Moodboard"
                    onClick={() => handleDeleteBoard(activeBoard.moodboard_id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Quick Add Toolbar & Category Filters */}
          <div className="pt-2 border-t border-border flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            {/* Quick Add Pills */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                className="h-8 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-xs gap-1.5 shadow-sm"
                onClick={() => {
                  setEditingItemId(null);
                  setItemForm({
                    type: "image",
                    title: "",
                    text_content: "",
                    category: "Dekorasi",
                    image_url: "",
                    image_id: "",
                    vendor: "",
                    estimated_cost: 0,
                    width: 6,
                    height: 300,
                  });
                  setAddItemOpen(true);
                }}
              >
                <ImageIcon className="h-3.5 w-3.5" /> + Foto & Media
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="h-8 rounded-full bg-muted hover:bg-accent text-foreground text-xs gap-1.5"
                onClick={() => {
                  setEditingItemId(null);
                  setItemForm({
                    type: "text",
                    title: "",
                    text_content: "",
                    category: "Dekorasi",
                    image_url: "",
                    image_id: "",
                    vendor: "",
                    estimated_cost: 0,
                    width: 4,
                    height: 240,
                  });
                  setAddItemOpen(true);
                }}
              >
                <FileText className="h-3.5 w-3.5 text-primary" /> + Catatan
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="h-8 rounded-full bg-muted hover:bg-accent text-foreground text-xs gap-1.5"
                onClick={() => setEditBoardOpen(true)}
              >
                <Palette className="h-3.5 w-3.5 text-primary" /> + Palet Warna
              </Button>
            </div>

            {/* Category Filter Pills (hidden scrollbar track like sidebar) */}
            <div className="flex items-center gap-1.5 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden pb-1 lg:pb-0">
              {categories.map((cat) => {
                const count =
                  cat === "Semua"
                    ? items.length
                    : items.filter(
                        (i) => (i.category || "").toLowerCase() === cat.toLowerCase()
                      ).length;
                const isSelected = selectedCategory === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3 h-7 rounded-full text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap transition-colors ${
                      isSelected
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "bg-muted text-muted-foreground hover:text-foreground hover:bg-accent"
                    }`}
                  >
                    {cat} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Main Canvas Workspace */}
      <div
        ref={canvasContainerRef}
        onMouseDown={handleCanvasMouseDown}
        onMouseMove={handleCanvasMouseMove}
        onMouseUp={handleCanvasMouseUp}
        onMouseLeave={handleCanvasMouseUp}
        className={`flex-1 relative p-4 md:p-8 overflow-auto min-h-[750px] select-none ${
          isPanMode
            ? isDraggingCanvas
              ? "cursor-grabbing"
              : "cursor-grab"
            : "cursor-default"
        }`}
      >
        {/* Dot Matrix Ambient Grid Background */}
        {showGrid && (
          <div className="absolute inset-0 pointer-events-none opacity-30 bg-[radial-gradient(#727971_1px,transparent_1px)] [background-size:24px_24px]"></div>
        )}

        {/* Floating Canvas Indicator */}
        <div className="absolute top-4 left-6 z-10 hidden sm:flex items-center gap-2 bg-card/80 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-border shadow-sm text-[11px] text-muted-foreground">
          <Hand className="h-3.5 w-3.5 text-primary" />
          <span>CANVAS AKTIF • MODE TANGAN (KLIK & GESER TO PAN)</span>
        </div>

        {/* Floating Canvas Controls Toolbar (Zoom, Grid, Hand, Undo/Redo) */}
        <div className="absolute top-4 right-6 z-20 flex items-center bg-card/90 backdrop-blur-md rounded-full border border-border p-1 shadow-md gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 rounded-full ${
              historyStack.length === 0
                ? "text-muted-foreground/40 cursor-not-allowed"
                : "text-muted-foreground hover:bg-accent"
            }`}
            title="Undo (Kembalikan langkah sebelumnya)"
            onClick={handleUndo}
            disabled={historyStack.length === 0}
          >
            <Undo2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 rounded-full ${
              redoStack.length === 0
                ? "text-muted-foreground/40 cursor-not-allowed"
                : "text-muted-foreground hover:bg-accent"
            }`}
            title="Redo (Maju ke langkah selanjutnya)"
            onClick={handleRedo}
            disabled={redoStack.length === 0}
          >
            <Redo2 className="h-4 w-4" />
          </Button>
          <div className="w-px h-4 bg-border mx-0.5"></div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full text-muted-foreground hover:bg-accent"
            onClick={() => setZoomLevel((z) => Math.max(60, z - 10))}
            title="Zoom Out (-)"
          >
            -
          </Button>
          <span className="text-xs px-1.5 text-center min-w-[42px] font-semibold text-foreground">
            {zoomLevel}%
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full text-muted-foreground hover:bg-accent"
            onClick={() => setZoomLevel((z) => Math.min(150, z + 10))}
            title="Zoom In (+)"
          >
            +
          </Button>
          <div className="w-px h-4 bg-border mx-0.5"></div>
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 rounded-full ${
              showGrid ? "text-primary bg-accent" : "text-muted-foreground"
            }`}
            onClick={() => setShowGrid((g) => !g)}
            title="Toggle Grid Canvas"
          >
            <Grid2X2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 rounded-full ${
              isPanMode ? "text-primary bg-accent" : "text-muted-foreground"
            }`}
            onClick={() => setIsPanMode((p) => !p)}
            title={
              isPanMode
                ? "Mode Tangan (Drag/Geser Canvas) Aktif"
                : "Aktifkan Mode Tangan (Drag Canvas)"
            }
          >
            <Hand className="h-4 w-4" />
          </Button>
        </div>

        {/* Interactive Grid Canvas Layout */}
        <div
          className="relative z-10 grid grid-cols-1 md:grid-cols-12 gap-6 w-full max-w-7xl mx-auto transition-transform duration-200"
          style={{ transform: `scale(${zoomLevel / 100})`, transformOrigin: "top center" }}
        >
          {/* Default Hero Board Swatch Header Card if active board has colors */}
          {activeBoard?.colors && activeBoard.colors.length > 0 && selectedCategory === "Semua" && (
            <div className="md:col-span-12 bg-card rounded-xl shadow-sm border border-border p-5 flex flex-col md:flex-row items-center justify-between gap-6 group hover:shadow-md transition-all">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="border-primary/40 text-primary bg-primary/5">
                    Harmoni Warna Utama
                  </Badge>
                </div>
                <h3 className="font-serif text-xl font-medium text-card-foreground">
                  {activeBoard.concept || "Garden Fresh & Warm Champagne"}
                </h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed max-w-xl">
                  {activeBoard.description ||
                    "Perpaduan rona hijau botani hutan tropis dengan kemilau gading sutra dan aksen emas klasik."}
                </p>
              </div>

              {/* Swatch Blocks */}
              <div className="flex items-center gap-3">
                {activeBoard.colors.map((c, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col items-center gap-1 group/swatch cursor-pointer"
                    onClick={() => copyHex(c.hex_code)}
                  >
                    <div
                      className="w-16 h-16 rounded-lg shadow-sm flex items-end justify-center pb-1 hover:scale-105 transition-transform"
                      style={{ backgroundColor: c.hex_code }}
                    >
                      <span className="text-[9px] font-mono text-white bg-black/40 px-1 rounded opacity-0 group-hover/swatch:opacity-100 transition-opacity">
                        Copy
                      </span>
                    </div>
                    <span className="text-[11px] font-medium text-foreground">
                      {c.color_name}
                    </span>
                    <span className="text-[10px] font-mono text-muted-foreground">
                      {c.hex_code}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Items Canvas Grid Cards */}
          {filteredItems.map((item, index) => {
            const spanClass =
              item.width >= 7
                ? "md:col-span-7"
                : item.width >= 5
                ? "md:col-span-5"
                : item.width >= 4
                ? "md:col-span-4"
                : "md:col-span-3";

            const isBeingDragged = draggedItemIndex === index;
            const isHoveredTarget = dragOverItemIndex === index;

            if (item.type === "text") {
              return (
                <div
                  key={item.item_id}
                  draggable={!isPanMode}
                  onDragStart={(e) => handleCardDragStart(e, index)}
                  onDragEnter={(e) => handleCardDragEnter(e, index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnd={handleCardDragEnd}
                  className={`${spanClass} bg-card rounded-xl shadow-sm border ${
                    isHoveredTarget
                      ? "border-2 border-dashed border-primary scale-[1.02]"
                      : "border-border"
                  } p-6 relative group hover:shadow-md transition-all ${
                    isBeingDragged ? "opacity-40 scale-95" : ""
                  } -rotate-1 hover:rotate-0 cursor-grab active:cursor-grabbing`}
                >
                  {/* Gilded Pin */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-accent border border-primary/40 shadow-sm flex items-center justify-center">
                    <Pin className="h-3.5 w-3.5 text-primary" />
                  </div>

                  <div className="pt-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1">
                        <GripVertical className="h-4 w-4 text-muted-foreground/60 cursor-grab opacity-60 group-hover:opacity-100" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-primary">
                          {item.category || "Memo"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleStartEditItem(item)}
                          className="p-1 text-muted-foreground hover:text-primary"
                          title="Edit Memo"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteItem(item.item_id)}
                          className="p-1 text-muted-foreground hover:text-red-600"
                          title="Hapus Memo"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    <blockquote className="font-serif italic text-base text-foreground leading-relaxed">
                      &ldquo;{item.text_content}&rdquo;
                    </blockquote>

                    {item.title && (
                      <div className="mt-4 pt-2 border-t border-border/50 flex items-center justify-between text-[11px] text-muted-foreground">
                        <span className="font-medium text-foreground">{item.title}</span>
                        {item.vendor && <span>Vendor: {item.vendor}</span>}
                      </div>
                    )}
                  </div>
                </div>
              );
            }

            // Image Card
            return (
              <div
                key={item.item_id}
                draggable={!isPanMode}
                onDragStart={(e) => handleCardDragStart(e, index)}
                onDragEnter={(e) => handleCardDragEnter(e, index)}
                onDragOver={(e) => e.preventDefault()}
                onDragEnd={handleCardDragEnd}
                className={`${spanClass} bg-card rounded-xl shadow-sm border ${
                  isHoveredTarget
                    ? "border-2 border-dashed border-primary scale-[1.02]"
                    : "border-border"
                } p-4 flex flex-col group hover:shadow-md transition-all relative ${
                  isBeingDragged ? "opacity-40 scale-95" : ""
                } cursor-grab active:cursor-grabbing`}
              >
                {/* Ribbon & Category Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-1.5">
                    <GripVertical className="h-4 w-4 text-muted-foreground/60 cursor-grab opacity-60 group-hover:opacity-100" />
                    <Badge
                      variant="secondary"
                      className="bg-muted text-primary text-[10px] font-semibold uppercase tracking-wider"
                    >
                      {item.category || "Referensi"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {item.image_url && (
                      <button
                        onClick={() => setPreviewImage(item.image_url)}
                        className="p-1 text-muted-foreground hover:text-primary"
                        title="Perbesar"
                      >
                        <Maximize2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleStartEditItem(item)}
                      className="p-1 text-muted-foreground hover:text-primary"
                      title="Edit Elemen"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteItem(item.item_id)}
                      className="p-1 text-muted-foreground hover:text-red-600"
                      title="Hapus"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {/* Image Container */}
                {item.image_url && (
                  <div className="relative w-full h-64 rounded-lg overflow-hidden bg-muted mb-3">
                    <img
                      src={item.image_url}
                      alt={item.title || "Referensi"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}

                {/* Details */}
                <h4 className="font-serif text-lg font-medium text-card-foreground">
                  {item.title || "Referensi Visual"}
                </h4>
                {item.text_content && (
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {item.text_content}
                  </p>
                )}

                {/* Footer Metadata */}
                {(item.vendor || item.estimated_cost > 0) && (
                  <div className="mt-3 pt-2 border-t border-border flex items-center justify-between text-[11px] text-muted-foreground">
                    {item.vendor && <span>Vendor: {item.vendor}</span>}
                    {item.estimated_cost > 0 && (
                      <span className="font-semibold text-primary">
                        Estimasi {idr(item.estimated_cost)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {filteredItems.length === 0 && (
            <div className="md:col-span-12 py-16 text-center border-2 border-dashed border-border rounded-xl bg-card/50">
              <Flower2 className="h-10 w-10 text-primary/40 mx-auto mb-3" />
              <h3 className="font-serif text-lg font-medium text-card-foreground">
                Canvas Moodboard Masih Kosong
              </h3>
              <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
                Tambahkan foto inspirasi, catatan konsep, atau gunakan Weddly AI Muse untuk memuat rekomendasi visual otomatis.
              </p>
              <div className="mt-4 flex items-center justify-center gap-3">
                <Button
                  size="sm"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs gap-1.5"
                  onClick={() => {
                    setItemForm((prev) => ({ ...prev, type: "image" }));
                    setAddItemOpen(true);
                  }}
                >
                  <Plus className="h-3.5 w-3.5" /> Tambah Referensi Pertama
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-primary text-primary text-xs gap-1.5"
                  onClick={() => setAiDrawerOpen(true)}
                >
                  <Sparkles className="h-3.5 w-3.5 text-primary" /> Tanya AI Muse
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialog: Create Moodboard */}
      <Dialog open={createBoardOpen} onOpenChange={setCreateBoardOpen}>
        <DialogContent className="max-w-md bg-card border-border max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-card-foreground">
              Buat Wedding Moodboard Baru
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-1 space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold">Judul Moodboard</Label>
              <Input
                placeholder="misal: Intimate Forest Wedding"
                value={boardForm.title}
                onChange={(e) => setBoardForm({ ...boardForm, title: e.target.value })}
                className="mt-1 border-border"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold">Deskripsi / Tema</Label>
              <Textarea
                placeholder="Tuliskan gambaran umum konsep visual pernikahan..."
                value={boardForm.description}
                onChange={(e) => setBoardForm({ ...boardForm, description: e.target.value })}
                className="mt-1 border-border text-xs"
                rows={3}
              />
            </div>

            <div>
              <Label className="text-xs font-semibold">Pilih Palet Warna Default</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {PREDEFINED_PALETTES.map((pal) => (
                  <div
                    key={pal.name}
                    onClick={() => setBoardForm({ ...boardForm, colors: pal.colors, concept: pal.name })}
                    className={`p-2.5 rounded-lg border text-xs cursor-pointer transition-all ${
                      boardForm.concept === pal.name
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-muted-foreground"
                    }`}
                  >
                    <div className="font-medium text-foreground text-[11px] mb-1">
                      {pal.name}
                    </div>
                    <div className="flex gap-1">
                      {pal.colors.map((c, idx) => (
                        <span
                          key={idx}
                          className="w-4 h-4 rounded-full border border-black/10"
                          style={{ backgroundColor: c.hex_code }}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateBoardOpen(false)}>
              Batal
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={handleCreateBoard}
              disabled={saving}
            >
              {saving ? "Menyimpan…" : "Buat Moodboard"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Edit Moodboard Details & Colors */}
      <Dialog open={editBoardOpen} onOpenChange={setEditBoardOpen}>
        <DialogContent className="max-w-lg bg-card border-border max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-card-foreground">
              Ubah Informasi Moodboard
            </DialogTitle>
          </DialogHeader>

          {activeBoard && (
            <div className="flex-1 overflow-y-auto pr-1 space-y-4 py-2">
              <div>
                <Label className="text-xs font-semibold">Judul Moodboard</Label>
                <Input
                  value={activeBoard.title || ""}
                  onChange={(e) => setActiveBoard({ ...activeBoard, title: e.target.value })}
                  className="mt-1 border-border"
                />
              </div>

              <div>
                <Label className="text-xs font-semibold">Konsep Visual</Label>
                <Input
                  value={activeBoard.concept || ""}
                  onChange={(e) => setActiveBoard({ ...activeBoard, concept: e.target.value })}
                  className="mt-1 border-border"
                />
              </div>

              <div>
                <Label className="text-xs font-semibold">Deskripsi Detail</Label>
                <Textarea
                  value={activeBoard.description || ""}
                  onChange={(e) => setActiveBoard({ ...activeBoard, description: e.target.value })}
                  className="mt-1 border-border text-xs"
                  rows={2}
                />
              </div>

              {/* Color Swatch Editor */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs font-semibold">Palet Warna Utama (4 Warna)</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[11px] text-primary"
                    onClick={() => {
                      const nextPal = PREDEFINED_PALETTES[0].colors;
                      setActiveBoard({ ...activeBoard, colors: nextPal });
                    }}
                  >
                    Reset Palet Default
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {(activeBoard.colors || []).map((col, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                      <input
                        type="color"
                        value={col.hex_code || "#ffffff"}
                        onChange={(e) => {
                          const updated = [...activeBoard.colors];
                          updated[idx] = { ...updated[idx], hex_code: e.target.value };
                          setActiveBoard({ ...activeBoard, colors: updated });
                        }}
                        className="w-8 h-8 rounded border-none cursor-pointer shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <Input
                          value={col.color_name || ""}
                          onChange={(e) => {
                            const updated = [...activeBoard.colors];
                            updated[idx] = { ...updated[idx], color_name: e.target.value };
                            setActiveBoard({ ...activeBoard, colors: updated });
                          }}
                          placeholder="Nama Warna"
                          className="h-7 text-xs bg-card border-border"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditBoardOpen(false)}>
              Batal
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={handleUpdateBoard}
              disabled={saving}
            >
              {saving ? "Menyimpan…" : "Simpan Perubahan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Add/Edit Canvas Item */}
      <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
        <DialogContent className="max-w-md bg-card border-border max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-card-foreground">
              {editingItemId ? "Ubah Elemen Canvas" : "Tambah Elemen ke Canvas"}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-1 space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold">Tipe Elemen</Label>
              <Select
                value={itemForm.type}
                onValueChange={(val) => setItemForm({ ...itemForm, type: val })}
              >
                <SelectTrigger className="mt-1 border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="image">Foto & Media</SelectItem>
                  <SelectItem value="text">Catatan / Memo Eksklusif</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold">Kategori Referensi</Label>
              <Select
                value={itemForm.category}
                onValueChange={(val) => setItemForm({ ...itemForm, category: val })}
              >
                <SelectTrigger className="mt-1 border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.filter((c) => c !== "Semua").map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {itemForm.type === "image" && (
              <div>
                <Label className="text-xs font-semibold">Unggah File Gambar</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-dashed border-primary text-primary gap-2 h-10"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                  >
                    {uploadingFile ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    <span>
                      {uploadingFile ? "Mengunggah..." : "Pilih Gambar (JPG, PNG, WebP)"}
                    </span>
                  </Button>
                </div>

                <div className="mt-2 text-center text-xs text-muted-foreground">atau masukkan URL:</div>
                <Input
                  placeholder="https://..."
                  value={itemForm.image_url}
                  onChange={(e) => setItemForm({ ...itemForm, image_url: e.target.value })}
                  className="mt-1 border-border text-xs"
                />

                {itemForm.image_url && (
                  <div className="mt-2 h-32 rounded-lg overflow-hidden border border-border">
                    <img
                      src={itemForm.image_url}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold">Judul / Label</Label>
              <Input
                placeholder="misal: Pelaminan Arch Flowers"
                value={itemForm.title}
                onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                className="mt-1 border-border"
              />
            </div>

            {itemForm.type === "text" ? (
              <div>
                <Label className="text-xs font-semibold">Isi Catatan Memo</Label>
                <Textarea
                  placeholder="Tuliskan memo eksklusif perjamuan / instruksi vendor..."
                  value={itemForm.text_content}
                  onChange={(e) => setItemForm({ ...itemForm, text_content: e.target.value })}
                  className="mt-1 border-border text-xs"
                  rows={4}
                />
              </div>
            ) : (
              <div>
                <Label className="text-xs font-semibold">Keterangan Tambahan</Label>
                <Textarea
                  placeholder="Penjelasan singkat material / suasana pencahayaan..."
                  value={itemForm.text_content}
                  onChange={(e) => setItemForm({ ...itemForm, text_content: e.target.value })}
                  className="mt-1 border-border text-xs"
                  rows={2}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-semibold">Vendor (Opsional)</Label>
                <Input
                  placeholder="Rumah Flora"
                  value={itemForm.vendor}
                  onChange={(e) => setItemForm({ ...itemForm, vendor: e.target.value })}
                  className="mt-1 border-border text-xs"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Estimasi Biaya (Rp)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={itemForm.estimated_cost || ""}
                  onChange={(e) =>
                    setItemForm({
                      ...itemForm,
                      estimated_cost: Number(e.target.value) || 0,
                    })
                  }
                  className="mt-1 border-border text-xs"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Ukuran Kartu Canvas</Label>
              <Select
                value={String(itemForm.width || 6)}
                onValueChange={(val) => setItemForm({ ...itemForm, width: Number(val) })}
              >
                <SelectTrigger className="mt-1 border-border text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">Kecil (3 Kolom / 25%)</SelectItem>
                  <SelectItem value="4">Sedang (4 Kolom / 33%)</SelectItem>
                  <SelectItem value="5">Sedang-Besar (5 Kolom / 42%)</SelectItem>
                  <SelectItem value="6">Besar (6 Kolom / 50%)</SelectItem>
                  <SelectItem value="7">Utama (7 Kolom / 58%)</SelectItem>
                  <SelectItem value="12">Penuh (12 Kolom / 100%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddItemOpen(false)}>
              Batal
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={handleSaveItem}
              disabled={saving}
            >
              {saving
                ? "Menyimpan…"
                : editingItemId
                ? "Simpan Perubahan"
                : "Tambah Elemen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sheet / Drawer: Weddly AI Muse */}
      <Sheet open={aiDrawerOpen} onOpenChange={setAiDrawerOpen}>
        <SheetContent className="w-full sm:max-w-md bg-card border-l border-border overflow-y-auto">
          <SheetHeader className="pb-4 border-b border-border">
            <SheetTitle className="font-serif text-2xl text-card-foreground flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" /> Weddly AI Muse
            </SheetTitle>
            <SheetDescription className="text-xs text-muted-foreground">
              Rekomendasi visual & konsep pernikahan eksklusif bertenaga AI.
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs font-semibold">Konsep / Tema Pernikahan</Label>
              <Input
                value={aiForm.concept}
                onChange={(e) => setAiForm({ ...aiForm, concept: e.target.value })}
                placeholder="misal: Garden Glasshouse"
                className="mt-1 border-border text-xs"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold">Warna Favorit</Label>
              <Input
                value={aiForm.color_preference}
                onChange={(e) => setAiForm({ ...aiForm, color_preference: e.target.value })}
                placeholder="misal: Sage Green & Warm Gold"
                className="mt-1 border-border text-xs"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold">Gaya Visual</Label>
              <Input
                value={aiForm.style_preference}
                onChange={(e) => setAiForm({ ...aiForm, style_preference: e.target.value })}
                placeholder="misal: High Fashion Editorial Atelier"
                className="mt-1 border-border text-xs"
              />
            </div>

            <Button
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
              onClick={handleFetchAiRecommendations}
              disabled={aiLoading}
            >
              {aiLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
              <span>{aiLoading ? "Menganalisis Visi Visual…" : "Hasilkan Rekomendasi AI"}</span>
            </Button>

            {aiResult && (
              <div className="mt-6 space-y-4 pt-4 border-t border-border">
                <div>
                  <h4 className="font-serif font-medium text-lg text-card-foreground">
                    {aiResult.concept_title}
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {aiResult.concept_summary}
                  </p>
                </div>

                {/* AI Colors */}
                {aiResult.color_palette && (
                  <div>
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Rekomendasi Warna
                    </Label>
                    <div className="flex gap-2 mt-2">
                      {aiResult.color_palette.map((c, idx) => (
                        <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                          <div
                            className="w-full h-10 rounded border border-black/10"
                            style={{ backgroundColor: c.hex_code }}
                          />
                          <span className="text-[10px] font-medium truncate w-full text-center">
                            {c.color_name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Suggested Items */}
                {aiResult.suggested_items && (
                  <div>
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Rekomendasi Elemen Visual
                    </Label>
                    <div className="space-y-2 mt-2">
                      {aiResult.suggested_items.map((sugg, idx) => (
                        <div
                          key={idx}
                          className="p-3 bg-muted rounded-lg border border-border text-xs"
                        >
                          <div className="flex items-center justify-between font-semibold text-card-foreground">
                            <span>{sugg.title}</span>
                            <Badge variant="outline" className="text-[9px] uppercase border-primary/40 text-primary">
                              {sugg.category}
                            </Badge>
                          </div>
                          <p className="text-muted-foreground text-[11px] mt-1">
                            {sugg.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2 font-semibold"
                  onClick={handleApplyAiRecommendation}
                  disabled={saving}
                >
                  <Check className="h-4 w-4" /> Terapkan ke Canvas Moodboard
                </Button>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Fullscreen Preview Lightbox Modal */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-4xl p-2 bg-black/90 border-none">
          <div className="relative w-full h-[80vh] flex items-center justify-center">
            {previewImage && (
              <img
                src={previewImage}
                alt="Preview Detail"
                className="max-w-full max-h-full object-contain rounded"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
